import { useCallback, useEffect, useRef } from 'react';

import { QuickstartCard } from './QuickstartCard';
import { QuickstartTheme, getWishlistButtonImage } from '../types/theme';
import { buildQuickstartSteamUrl } from '../utils/steamUrls';

interface QuickstartThankYouRedirectProps {
  theme: QuickstartTheme;
  autoRedirect?: boolean;
  onRedirect?: (steamUrl: string) => void;
}

/**
 * QuickstartThankYouRedirect Component
 *
 * Displays thank you message with logos and a Steam button image that redirects when clicked.
 * Follows the same layout as QuickstartActionPage: studio logo, text, game logo, button.
 *
 * TODO: Import actual Steam button image at /quickstart/{slug}/steam-button.png
 */
export const QuickstartThankYouRedirect = ({ theme, autoRedirect, onRedirect }: QuickstartThankYouRedirectProps) => {
  const handleCheckItOut = useCallback(() => {
    if (!theme.steamUrl) {
      return;
    }

    const finalUrl = buildQuickstartSteamUrl(theme.steamUrl);

    // Send analytics callback
    if (onRedirect) {
      onRedirect(finalUrl);
    }

    // Open in new tab instead of redirecting (prevents canceling analytics request)
    // Works on both mobile and desktop
    window.open(finalUrl, '_blank', 'noopener,noreferrer');
  }, [onRedirect, theme.steamUrl]);

  const hasAutoRedirected = useRef(false);

  useEffect(() => {
    if (!autoRedirect || hasAutoRedirected.current) {
      return;
    }

    if (!theme.steamUrl) {
      return;
    }

    hasAutoRedirected.current = true;

    // Auto-redirect: Use location.href instead of window.open to avoid popup blockers
    // Safari, Opera, and other browsers block window.open() when not triggered by direct user action
    const finalUrl = buildQuickstartSteamUrl(theme.steamUrl);

    // Send analytics callback before redirect
    if (onRedirect) {
      onRedirect(finalUrl);
    }

    // Delay to ensure analytics can be sent (500ms is conservative for network latency)
    // Analytics uses fetch with keepalive:true, which helps during redirects
    setTimeout(() => {
      window.location.href = finalUrl;
    }, 500);
  }, [autoRedirect, onRedirect, theme.steamUrl]);

  return (
    <div
      className='flex min-h-screen items-center justify-center overflow-auto p-4'
      style={{
        backgroundImage: theme.pageBackgroundImage ? `url(${theme.pageBackgroundImage})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <QuickstartCard theme={theme} showHeader={false}>
        <div className='flex flex-col items-center text-center'>
          {/* Studio Logo */}
          {theme.studioLogo && <img src={theme.studioLogo} alt={theme.studioName} className='mt-4 h-16' />}

          {/* Thank You Text */}
          <div className='mt-16'>
            <div className='font-["Inter"] text-3xl font-semibold' style={{ color: theme.primaryColor }}>
              THANK YOU!
            </div>
            <div className='mt-2 font-["Inter"] text-2xl font-semibold text-white'>NOW GO WISHLIST</div>
          </div>

          {/* Game Logo */}
          {theme.gameLogo && <img src={theme.gameLogo} alt={theme.gameName} className='mt-8 h-20' />}

          {theme.steamUrl && (() => {
            const buttonImage = getWishlistButtonImage(theme);
            return (
              <img
                src={buttonImage.src}
                onClick={handleCheckItOut}
                className='mt-8 h-16 cursor-pointer transition-opacity hover:opacity-80'
                role='button'
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleCheckItOut();
                  }
                }}
                alt={buttonImage.alt}
              />
            );
          })()}
        </div>
      </QuickstartCard>
    </div>
  );
};
